import { events } from 'aws-amplify/data';
import { fetchAuthSession } from 'aws-amplify/auth';
import { logger } from 'src/logger';
import { EventsApiContextType } from 'src/components/context/EventsApiContextModels';

const RESPONSE_TIMEOUT_MS = 60000; // 60 seconds timeout for responses
const ERROR_MESSAGE = `No response received within ${RESPONSE_TIMEOUT_MS / 1000} seconds. Please cut us a ticket to investigate`;

/**
 * Gets the current access token from AWS Cognito session
 * @returns Promise<string | null> - Current access token or null if unavailable
 */
const getCurrentAccessToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession(); // Did not add in context because need to access in real-time to avoid expiration
    const token = session.tokens?.accessToken?.toString() || null;
    if (!token) {
      logger.warn('No access token available for Meeting Events API');
    }
    return token;
  } catch (error) {
    logger.warn('Error fetching current access token:', error as Error);
    return null;
  }
};

export interface MeetingParseRequest {
  sessionId: string;
  userId: string;
  meetingData: string; // HTML content from parsed Word doc
  customPrompt?: string;
  jwtToken: string | null;
  fileFormat?: string; // File format: "docx", "jpg", "jpeg", "png"
  imageData?: string; // Base64 image data with data URL prefix (data:image/<type>;base64,...)
}

export interface MeetingParseResponse {
  success: boolean;
  response: {
    Subject: string;
    Agenda: string;
    Duration: string;
    Recurrence?: string;
    OnBehalfOf?: string;
    Soonest: string;
    Latest: string;
    Timezone: string;
    CriticalAttendees: string[];
    RequiredAttendees: string[];
    OptionalAttendees: string[];
    MeetingData?: string; // HTML table for images, optional for backward compatibility
  };
}

export const parseMeetingDataParser = (
  sessionId: string,
  meetingData: string,
  customPrompt: string,
  eventsApiContext: EventsApiContextType,
  userAlias: string,
  fileFormat?: string,
  imageData?: string
): Promise<MeetingParseResponse> => {
  return new Promise(async (resolve, reject) => {
    try {
      if (meetingData === undefined || meetingData === null) {
        throw new Error('meetingData is a required parameter');
      }

      // Get fresh JWT token (or null if unavailable)
      const jwtToken = await getCurrentAccessToken();

      const currentRequestId = sessionId;

      // Cancel any existing meeting request
      const wasCanceled = eventsApiContext.cancelMeetingRequest?.();
      if (wasCanceled) {
        logger.info('Canceled previous meeting request');
      }

      // Store this as the latest request
      eventsApiContext.setLatestMeetingRequestId?.(currentRequestId);

      // Subscribe to response
      logger.info('Connecting to meeting subscription channel...');
      const pr = events.connect('create-meeting-subscriber/channel');

      let timeout: NodeJS.Timeout | undefined;
      let hasReceived = false;
      let subscriptionReady = false;

      // First set up the subscription
      await pr
        .then(async (channel) => {
          logger.info('Connected to meeting subscription channel');

          // Add timeout for no response
          timeout = setTimeout(() => {
            if (!hasReceived) {
              logger.error(`No response received within ${RESPONSE_TIMEOUT_MS}ms for meeting parsing`);
              logger.error(`Expected sessionId: ${currentRequestId}`);
              hasReceived = true; // Prevent duplicate timeouts
              reject(new Error(ERROR_MESSAGE));
              eventsApiContext.cancelMeetingRequest?.();
            }
          }, RESPONSE_TIMEOUT_MS);

          // Store full request state for resource management
          eventsApiContext.setMeetingRequestState?.({
            id: currentRequestId,
            timestamp: Date.now(),
            channel,
            timeout
          });

          // Set up a promise that resolves when subscription is ready
          const subscriptionReadyPromise = new Promise<void>((resolveReady) => {
            // Add timeout fallback to prevent infinite waiting
            const readyTimeout = setTimeout(() => {
              if (!subscriptionReady) {
                subscriptionReady = true;
                logger.info('Subscription ready timeout reached (5s) - proceeding anyway');
                resolveReady();
              }
            }, 5000); // 5 second timeout

            channel.subscribe({
              next: (data) => {
                // Enhanced logging to debug the exact structure received
                logger.info('=== RAW EVENT RECEIVED ===');

                // Log specific fields for debugging
                if (data) {
                  logger.info('data.event exists: ' + !!data.event);
                  if (data.event) {
                    logger.info('data.event.sessionId: ' + data.event.sessionId);
                    logger.info('data.event.success: ' + data.event.success);
                    logger.info('data.event.isFinal: ' + data.event.isFinal);
                    logger.info('data.event.response exists: ' + !!data.event.response);
                  }
                }

                // Mark subscription as ready on first event
                if (!subscriptionReady) {
                  subscriptionReady = true;
                  clearTimeout(readyTimeout); // Clear the fallback timeout
                  logger.info('Subscription is now ready to receive events');
                  resolveReady(); // Signal that subscription is ready
                }

                // Get the latest request ID from context for comparison
                const latestRequestId = eventsApiContext.getLatestMeetingRequestId?.();
                const receivedSessionId = data.event?.sessionId;

                logger.info(`Comparing sessionIds: received="${receivedSessionId}" expected="${latestRequestId}"`);

                // Check if this is our response - also handle case where context might be lost
                const isMatchingSession =
                  receivedSessionId === latestRequestId || (latestRequestId === undefined && receivedSessionId === currentRequestId);

                if (isMatchingSession && receivedSessionId) {
                  hasReceived = true;
                  // Clear the timeout since we received a response
                  if (timeout) {
                    clearTimeout(timeout);
                    timeout = undefined;
                  }
                  logger.info('SessionId matches! Processing response...');

                  // Handle Lambda's actual response structure
                  const hasMessage = data.event?.message !== undefined;
                  const hasLegacySuccess = data.event?.success !== undefined;
                  const isFinalResponse = data.event?.isFinal === true;

                  logger.info('Response structure check:', {
                    hasMessage,
                    hasLegacySuccess,
                    isFinal: data.event?.isFinal,
                    messageLength: hasMessage ? data.event.message.length : 0
                  });

                  // Process final responses
                  if (isFinalResponse) {
                    let response: MeetingParseResponse;

                    if (hasMessage) {
                      // Lambda sends JSON string in message field - parse it
                      try {
                        const parsedMessage = JSON.parse(data.event.message);

                        // Check if backend returned an error response (e.g., guardrail block)
                        if (parsedMessage.success === false) {
                          const backendError = parsedMessage.error || 'Document processing failed on the server';
                          logger.error('Backend returned error response:', backendError);
                          if (timeout) {
                            clearTimeout(timeout);
                            timeout = undefined;
                          }
                          reject(new Error(backendError));
                          eventsApiContext.cancelMeetingRequest?.();
                          return;
                        }

                        response = {
                          success: true,
                          response: parsedMessage
                        };

                        logger.info('Successfully parsed Lambda message response:', {
                          hasSubject: !!parsedMessage.Subject,
                          requiredAttendeesCount: parsedMessage.RequiredAttendees?.length || 0,
                          optionalAttendeesCount: parsedMessage.OptionalAttendees?.length || 0
                        });
                      } catch (parseError) {
                        logger.error('Failed to parse Lambda message as JSON:', parseError as Error);
                        logger.error('Raw message:', data.event.message);
                        // Clear timeout on error
                        if (timeout) {
                          clearTimeout(timeout);
                          timeout = undefined;
                        }
                        reject(new Error(ERROR_MESSAGE));
                        eventsApiContext.cancelMeetingRequest?.();
                        return;
                      }
                    } else if (hasLegacySuccess) {
                      // Legacy format with success/response fields
                      response = {
                        success: data.event.success,
                        response: data.event.response || {
                          Subject: '',
                          RequiredAttendees: [],
                          OptionalAttendees: []
                        }
                      };

                      logger.info('Using legacy response format');
                    } else {
                      logger.error('Invalid response format - no message or success field');
                      logger.error('Received event structure: ' + JSON.stringify(data.event, null, 2));
                      // Clear timeout on error
                      if (timeout) {
                        clearTimeout(timeout);
                        timeout = undefined;
                      }
                      reject(new Error('Invalid response format - missing message and success fields'));
                      eventsApiContext.cancelMeetingRequest?.();
                      return;
                    }

                    logger.info('Meeting response processing completed successfully');
                    resolve(response);
                    eventsApiContext.cancelMeetingRequest?.();
                  } else {
                    logger.info('Received intermediate response, waiting for final response...');
                    logger.info('Event details:', {
                      isFinal: data.event?.isFinal,
                      hasMessage,
                      hasLegacySuccess
                    });
                  }
                } else if (data.event?.sessionId) {
                  logger.warn(`Ignoring response for different sessionId: ${data.event.sessionId}`);
                  logger.warn(`Currently waiting for: ${latestRequestId} (current: ${currentRequestId})`);
                } else {
                  logger.warn('Received event without sessionId: ' + JSON.stringify(data, null, 2));
                }
              },
              error: (error) => {
                logger.error('Meeting Events Subscription error:', error);
                // Clear timeout on subscription error
                if (timeout) {
                  clearTimeout(timeout);
                  timeout = undefined;
                }
                reject(new Error(ERROR_MESSAGE));
                eventsApiContext.cancelMeetingRequest?.();
              }
            });
          });

          // Wait for subscription to be ready before publishing
          logger.info('Waiting for subscription to be ready...');
          await subscriptionReadyPromise;

          // Now that subscription is set up, publish the event
          const event = {
            sessionId: sessionId,
            userId: userAlias,
            meetingData: meetingData,
            customPrompt: customPrompt,
            jwtToken: jwtToken,
            fileFormat: fileFormat || 'docx', // Default to docx for backward compatibility
            imageData: imageData || undefined // Include imageData if provided (for image file uploads)
          };

          logger.info('Event structure being published:', {
            sessionId: event.sessionId,
            hasJwtToken: !!event.jwtToken,
            meetingDataLength: event.meetingData.length,
            customPrompt: event.customPrompt,
            fileFormat: event.fileFormat,
            hasImageData: !!imageData,
            imageDataLength: imageData ? imageData.length : 0
          });

          try {
            await events.post('create-meeting-publisher/channel', event as Parameters<typeof events.post>[1]);
            logger.info('Meeting parsing event published successfully to create-meeting-publisher/channel');
            logger.info('Now waiting for response on create-meeting-subscriber/channel...');
          } catch (publishError) {
            logger.error('Error publishing meeting parsing event:', publishError as Error);
            // Clear timeout on publish error
            if (timeout) {
              clearTimeout(timeout);
              timeout = undefined;
            }
            eventsApiContext.cancelMeetingRequest?.();
            reject(publishError);
          }
        })
        .catch((error) => {
          logger.error('Meeting subscription connection error:', error);
          // Clear timeout on connection error
          if (timeout) {
            clearTimeout(timeout);
            timeout = undefined;
          }
          eventsApiContext.cancelMeetingRequest?.();
          reject(error);
        });
    } catch (error) {
      logger.error(`Error in parseMeetingDataParser: ${error}`);
      eventsApiContext.cancelMeetingRequest?.();
      reject(error);
    }
  });
};
